import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/layout/PageContainer";
import { Card, CardBody } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Avatar } from "@/ui/Avatar";
import { LoadingState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { useLabels } from "@/lib/labels";
import { useCurrentUser } from "@/features/auth/auth.hooks";
import { useMyTasks } from "@/features/tasks/tasks.hooks";
import { useMyTeamProfile } from "@/features/team/team.hooks";

export function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { TEAM_ROLE, TASK_STATUS } = useLabels();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const profile = useMyTeamProfile();
  const { tasks, isLoading: tasksLoading, isError: tasksError, refetch: refetchTasks } = useMyTasks();

  if (userLoading) {
    return <PageContainer><LoadingState rows={4} /></PageContainer>;
  }

  const displayName = profile.member?.name ?? currentUser?.name ?? "—";
  const displayEmail = profile.member?.email ?? currentUser?.email ?? "—";
  const member = profile.member;

  const statusCounts = {
    TODO: tasks.filter((task) => task.status === "TODO").length,
    IN_PROGRESS: tasks.filter((task) => task.status === "IN_PROGRESS").length,
    ON_HOLD: tasks.filter((task) => task.status === "ON_HOLD").length,
    DONE: tasks.filter((task) => task.status === "DONE").length,
  };

  return (
    <PageContainer>
      {/* Profile card — always visible; team details shown only when a TeamMember is linked */}
      <Card className="mb-4">
        <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <Avatar name={displayName} size={56} className="rounded-lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-display text-[22px] font-bold text-text">{displayName}</h1>
              {member && <Badge tone={TEAM_ROLE[member.role].tone}>{TEAM_ROLE[member.role].label}</Badge>}
              {member && (
                <Badge tone={member.status === "ACTIVE" ? "success" : member.status === "ON_LEAVE" ? "warning" : "danger"} dot>
                  {member.status === "ACTIVE" ? t("team.status.active") : member.status === "ON_LEAVE" ? t("team.status.onLeave") : t("team.status.inactive")}
                </Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-text2">{displayEmail}</p>
            {member?.phone && <p className="text-xs text-text3">{member.phone}</p>}
            {!member && !profile.isLoading && (
              <p className="mt-1 text-xs text-text3 italic">{t("profile.adminNoTeamProfileNote")}</p>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Task summary — shown for all users; admins with no assigned tasks see zero counts */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-bold text-text">{t("profile.myTasksSummary")}</h2>
            <Button size="sm" onClick={() => navigate("/tasks")}>{t("profile.goToMyTasks")}</Button>
          </div>

          {tasksError && <ApiFallbackBanner onRetry={() => refetchTasks()} />}

          {tasksLoading ? (
            <LoadingState rows={2} />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(["TODO", "IN_PROGRESS", "ON_HOLD", "DONE"] as const).map((s) => (
                <div key={s} className="rounded-md border border-border px-3 py-3 text-center">
                  <div className="text-2xl font-bold text-text">{statusCounts[s]}</div>
                  <div className="mt-0.5">
                    <Badge tone={TASK_STATUS[s].tone} dot>{TASK_STATUS[s].label}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </PageContainer>
  );
}
